/**
 * 検討の木。 サーバ (`mixed-do::study`) が正本で、 ここは読むだけの純粋関数。
 *
 * `analysis` のキーは局面の id。 ROOT は開始局面、 それ以外はその手を指した後の局面。
 */
import type { EngineApi, GameId } from '../../wasm';

export const ROOT = 'root';

export interface StudyNode {
  id: string;
  parent: string;
  mv: unknown;
  main: boolean;
  memo: string;
  deleted: boolean;
  by: string;
  /** 入れた順。 */
  seq: number;
}

export interface Candidate {
  mv: unknown;
  score: number;
  mate_in: number | null;
  pv: unknown[];
}

export interface DiceRow {
  a: number;
  b: number;
  score: number;
  mate_in: number | null;
}

/** 1 局面の解析結果。 `score` は `side` から見た値。 */
export interface PositionAnalysis {
  side: 'First' | 'Second';
  depth: number;
  total: number;
  candidates: Candidate[];
  dice?: DiceRow[];
}

export interface BoardTree {
  nodes: Record<string, StudyNode>;
  analysis: Record<string, { depth: number; data: PositionAnalysis }>;
}

export interface BoardMeta {
  board: number;
  game_id: GameId;
  result: 'First' | 'Second' | 'Draw' | null;
  final_state: unknown;
  first_seat: string | null;
}

export interface StudyMeta {
  title: string;
  host_seat: string | null;
  outcome: string | null;
  /** [host, guest] の表示名。 */
  players: string[];
  boards: BoardMeta[];
  creator_sub: string | null;
  creator_seat: string | null;
}

/**
 * 盤を見る側。 対局したときの向きに合わせる。 作った人以外は、 感想戦の相手として
 * 反対の席から見る。
 */
export function viewSideOf(meta: StudyMeta, board: BoardMeta, you: string | null): 'First' | 'Second' {
  if (!meta.creator_seat || !board.first_seat) return 'First';
  const creatorIsFirst = board.first_seat === meta.creator_seat;
  return creatorIsFirst === (you === meta.creator_sub) ? 'First' : 'Second';
}

export interface Cursor {
  board: number | null;
  node: string;
}

/** 親 → 子。 本譜を先頭に、 あとは入れた順に置く。 削除済みは含めない。 */
export function childrenOf(tree: BoardTree): Map<string, StudyNode[]> {
  const out = new Map<string, StudyNode[]>();
  for (const n of Object.values(tree.nodes)) {
    if (n.deleted) continue;
    const list = out.get(n.parent);
    if (list) list.push(n);
    else out.set(n.parent, [n]);
  }
  for (const list of out.values()) {
    list.sort((a, b) => Number(b.main) - Number(a.main) || a.seq - b.seq || a.id.localeCompare(b.id));
  }
  return out;
}

/** 開始局面から `id` までの手。 */
export function pathTo(tree: BoardTree, id: string): StudyNode[] {
  const out: StudyNode[] = [];
  let at = id;
  while (at !== ROOT) {
    const n = tree.nodes[at];
    if (!n) break;
    out.push(n);
    at = n.parent;
  }
  return out.reverse();
}

export function mainLine(tree: BoardTree): StudyNode[] {
  const kids = childrenOf(tree);
  const line: StudyNode[] = [];
  let at = ROOT;
  for (;;) {
    const next = kids.get(at)?.find((n) => n.main);
    if (!next) return line;
    line.push(next);
    at = next.id;
  }
}

/** 対局のある盤で最初に開く局面。 本譜の終局、 本譜がなければ開始局面。 */
export function finalNode(tree: BoardTree): string {
  return mainLine(tree).at(-1)?.id ?? ROOT;
}

/** ツリーの幹。 本譜をたどり、 その先は最初に入れた手をたどる。 */
export function spineLine(tree: BoardTree): StudyNode[] {
  const kids = childrenOf(tree);
  const line: StudyNode[] = [];
  let next = kids.get(ROOT)?.[0];
  while (next) {
    line.push(next);
    next = kids.get(next.id)?.[0];
  }
  return line;
}

/** 手数やグラフの基準にする手順。 本譜がない検討では幹を使う。 */
export function baseLine(tree: BoardTree): StudyNode[] {
  const main = mainLine(tree);
  return main.length > 0 ? main : spineLine(tree);
}

export interface TreeRow {
  node: StudyNode;
  /** 開始局面からの手数。 */
  ply: number;
  /** 字下げの段。 0 が幹。 */
  lane: number;
  /** この行が属する幹の手数。 */
  anchor: number;
  /** 親の線から分かれた最初の行。 */
  start: boolean;
  /** この線の最後の行。 */
  end: boolean;
  /** この行の上端で親の線から分かれる変化の先頭。 */
  forks: Wire[];
  /** この行を縦に通り抜ける、 まだ先に出る変化の先頭。 */
  passes: Wire[];
}

export interface Wire {
  id: string;
  lane: number;
}

/** 表示する行。 幹の手の直後に、 同じ親から分かれた変化を字下げして並べる。 */
export function treeRows(tree: BoardTree): TreeRow[] {
  const kids = childrenOf(tree);
  const rows: TreeRow[] = [];
  const variation = (n: StudyNode, ply: number, lane: number, anchor: number, start: boolean) => {
    const next = kids.get(n.id) ?? [];
    rows.push({ node: n, ply, lane, anchor, start, end: next.length === 0, forks: [], passes: [] });
    next.forEach((c, i) => {
      variation(c, ply + 1, lane + (i === 0 ? 0 : 1), anchor, i > 0);
    });
  };
  spineLine(tree).forEach((n, i) => {
    rows.push({ node: n, ply: i + 1, lane: 0, anchor: i + 1, start: false, end: false, forks: [], passes: [] });
    for (const sibling of kids.get(n.parent) ?? []) {
      if (sibling.id !== n.id) variation(sibling, i + 1, 1, i + 1, true);
    }
  });
  // 変化は親の直後の行の上端で分かれ、 手の横を通って自分の行まで下りる。
  const index = new Map(rows.map((r, i) => [r.node.id, i]));
  rows.forEach((r, i) => {
    if (!r.start) return;
    const wire = { id: r.node.id, lane: r.lane };
    const first = (index.get(r.node.parent) ?? -1) + 1;
    rows[first].forks.push(wire);
    for (let j = first; j < i; j++) rows[j].passes.push(wire);
  });
  return rows;
}

type BgState = { dice?: number[] } & Record<string, unknown>;

function bgDie(mv: unknown): number | null {
  if (typeof mv !== 'object' || mv === null) return null;
  const m = mv as { step?: { dice: number }; enter?: { dice: number }; bear_off?: { dice: number } };
  return m.step?.dice ?? m.enter?.dice ?? m.bear_off?.dice ?? null;
}

export function studyRoll(mv: unknown): [number, number] | null {
  if (typeof mv !== 'object' || mv === null || !('study_roll' in mv)) return null;
  return (mv as { study_roll: [number, number] }).study_roll;
}

/** 手番開始の局面に、 この手番で実際に使ったダイスを補う。 */
function bgFillTurn(state: BgState, line: StudyNode[], from: number): BgState {
  if ((state.dice ?? []).length > 0) return state;
  const dice: number[] = [];
  for (let i = from; i < line.length; i++) {
    const die = bgDie(line[i].mv);
    if (die === null) break;
    dice.push(die);
  }
  return dice.length ? { ...state, dice } : state;
}

/** `line[i]` を指した後の局面。 不正な手なら投げる。 */
function advance(game: GameId, api: EngineApi, before: unknown, line: StudyNode[], i: number): unknown {
  const n = line[i];
  if (game !== 'Backgammon') return api.apply(before, n.mv);
  const roll = studyRoll(n.mv);
  if (roll) {
    const [a, b] = roll;
    return { ...(before as BgState), dice: a === b ? [a, a, a, a] : [a, b] };
  }
  const state = bgFillTurn(before as BgState, line, i);
  const die = bgDie(n.mv);
  const cur = state.dice ?? [];
  return api.apply(die !== null && !cur.includes(die) ? { ...state, dice: [...cur, die] } : state, n.mv);
}

/** 手順をなぞった局面の列。 長さは `line.length + 1` で、 先頭が開始局面。 */
export function replayLine(game: GameId, api: EngineApi, line: StudyNode[]): unknown[] {
  const states: unknown[] = [api.init()];
  line.forEach((_, i) => {
    states.push(advance(game, api, states[i], line, i));
  });
  return states;
}

/** 各手を指す前の局面。 再現できない手とその先は含めない。 */
export function statesBefore(game: GameId, api: EngineApi, tree: BoardTree): Map<string, unknown> {
  const kids = childrenOf(tree);
  const out = new Map<string, unknown>();
  const visit = (parent: string, before: unknown) => {
    for (const n of kids.get(parent) ?? []) {
      out.set(n.id, before);
      try {
        visit(n.id, advance(game, api, before, [n], 0));
      } catch {
        // この手の先は読めない。
      }
    }
  };
  visit(ROOT, api.init());
  return out;
}

const playable = new WeakMap<BoardTree, BoardTree>();

/**
 * 再現できない変化を除いた木。 サーバは手の合法性を見ないので、 表示する側で落とす。
 * 本譜は信頼する。 木が変わるまで結果を再利用する。
 */
export function playableTree(game: GameId, api: EngineApi, tree: BoardTree): BoardTree {
  const cached = playable.get(tree);
  if (cached) return cached;
  const main = mainLine(tree);
  let result = tree;
  try {
    const states = replayLine(game, api, main);
    const kids = childrenOf(tree);
    const nodes: Record<string, StudyNode> = {};
    const visit = (n: StudyNode, before: unknown) => {
      let after: unknown;
      try {
        after = advance(game, api, before, [n], 0);
      } catch {
        return;
      }
      nodes[n.id] = n;
      for (const c of kids.get(n.id) ?? []) visit(c, after);
    };
    [ROOT, ...main.map((n) => n.id)].forEach((id, i) => {
      for (const c of kids.get(id) ?? []) {
        if (!c.main) visit(c, states[i]);
      }
    });
    for (const n of main) nodes[n.id] = n;
    result = { ...tree, nodes };
  } catch {
    // 本譜を再現できない盤 (手札の隠れたゲームなど) はそのまま見せる。
  }
  playable.set(tree, result);
  return result;
}
